import { Provider } from '@angular/core';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { Assignment } from '../../api/app-types/assignment';
import { DB_PHASES } from '../../api/app-types/phase.app';
import { CustomDateAdapter } from '../../core/adapters/custom-date-adapter';
import { CUSTOM_DATE_FORMATS } from '../../core/adapters/custom-date-format';
import { latestContactDate, latestLekEndDate, oldestContactDate } from './assignment.func';
import { Lek } from '../../api/app-types/lek.app';

export function formatDateToDisplay(value: Date) {
	return new Intl.DateTimeFormat('de-DE', {
		day: '2-digit',
		month: '2-digit',
		year: 'numeric',
		timeZone: 'UTC',
	}).format(value);
}

/**
 * Checks if the given date string is valid and different from the default DateOnly.MinValue ('0001-01-01'),
 * which is returned by the backend when no date is provided.
 * @param input - Date string to validate.
 * @returns true if the date is valid and not the default min value.
 */
export function isValidDate(input?: Date | string) {
	if (!input) {
		return false;
	}

	const date = new Date(input);
	if (Number.isNaN(date.getTime())) {
		return false;
	}

	// Check if it's the default '0001-01-01'
	const isMinDate = date.getFullYear() === 1 && date.getMonth() === 0 && date.getDate() === 1;

	return !isMinDate;
}

/**
 * Formats a date value (Date, string or null) into a 'YYYY-MM-DD' string.
 * This is required so the Backend, which uses DateOnly data type for 'dateOfBirth' property can correctly parse the date.
 * @returns A date string in 'yyyy-MM-dd' format or an empty string when the input is invalid.
 */
export function formatDateToISOString(value?: Date | string | null) {
	let date: Date;

	// Checks if the input value is a Date object
	// Copies the Date so the caller's value is not mutated by setHours below
	if (value instanceof Date) {
		date = new Date(value.getTime());
	}

	// Checks if the input value is a string
	else if (typeof value === 'string') {
		date = new Date(value);

		// Return an empty string is the date string is invalid
		if (isNaN(date.getTime())) {
			return;
		}
	}
	// Returns an empty string if the input value is neither a Date object nor a string
	else {
		return;
	}

	//Set time to 12:00 to have no timezone issues
	date.setHours(12, 0, 0, 0);

	// Extracts the year, month, and day from the date
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-based, so we add 1
	const day = String(date.getDate()).padStart(2, '0');

	// Return the formatted date string
	return `${year}-${month}-${day}`;
}

/**
 * Converts a date input into a valid Date object or null.
 * Returns null if the input is empty or equal to the backend's default "0001-01-01" or "9999-12-31" or its overflow.
 *
 * @param input The date value to normalize.
 * @returns A valid Date object or null.
 */
export function mapDateOrNull(input: string | Date | null | undefined): Date | null {
	if (!input) return null;
	const date = new Date(input);
	if (Number.isNaN(date.getTime())) return null;

	const y = date.getUTCFullYear();
	const m = date.getUTCMonth();
	const d = date.getUTCDate();

	const isMin = y === 1 && m === 0 && d === 1;
	const isMax = y === 9999 && m === 11 && d === 31;
	const isOverflow = y >= 10000;

	if (isMin || isMax || isOverflow) return null;

	return date;
}

/**
 * Updates the date by adding or subtracting a specified number of years.
 * @param date The date to update. If no date is provided, the current date is used.
 * @param years The number of years to add (can be negative).
 * @returns The updated date.
 */
export function updateYearsOfDate(years: number, date?: Date): Date {
	const updatedDate = new Date(date ?? new Date());
	// update the date by subtracting or adding the specified number of years
	updatedDate.setFullYear(updatedDate.getFullYear() + years);
	return updatedDate;
}

const STORED_DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/** Parses a stored yyyy-MM-dd day; rejects anything that is not a real calendar date (e.g. 2026-02-30). */
export function parseStoredDay(value: unknown): Date | null {
	if (typeof value !== 'string' || !STORED_DAY_PATTERN.test(value)) return null;
	const [year, month, day] = value.split('-').map(Number);
	const date = new Date(year, month - 1, day);
	return formatDateToISOString(date) === value ? date : null;
}

export const matDateProviders: Provider[] = [
	{ provide: DateAdapter, useClass: CustomDateAdapter },
	{ provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS },
	{ provide: MAT_DATE_LOCALE, useValue: 'de-DE' },
	{
		provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
		useValue: { subscriptSizing: 'dynamic' },
	},
];

/**
 * If you go from a specific phase into another phase, this function calculates the possible minimum date for the transition.
 * @param targetPhase ID of the to which we want to go
 * @param assignment The assignment which should make the transition to the phase
 * @param previousAssignment The previous, latest assignment of the client (if there is any)
 * @param leks An array of LEKs (if there is any)
 * @returns A date which is the oldest date that can be selected on the phase transition
 */
export function oldestPossibleDateForPhaseChange(
	targetPhase: DB_PHASES,
	assignment?: Assignment,
	previousAssignment?: Assignment,
	leks?: Lek[],
) {
	const threshold = new Date(new Date().setDate(new Date().getDate() - 7 * 6));
	const sixWeeksAgo = normalizeToStartOfDay(threshold);

	// P1 -> P2
	// Anmelden P1 -> P2: - 42 Tage, >= Beginn P1
	if (targetPhase === DB_PHASES.P2 && assignment?.phaseId === DB_PHASES.P1) {
		return minDateFromP1Start(assignment, previousAssignment, sixWeeksAgo);
	}

	// P1 -> P3
	// Anmelden P1 -> P3: - 42 Tage, >= Beginn P1
	if (targetPhase === DB_PHASES.P3 && assignment?.phaseId === DB_PHASES.P1) {
		return minDateFromP1Start(assignment, previousAssignment, sixWeeksAgo);
	}

	// P1 -> P4
	// Abmelden P1 -> P4: - 42 Tage, >= Beginn P1
	if (targetPhase === DB_PHASES.P4 && (assignment?.phaseId === DB_PHASES.P0 || assignment?.phaseId === DB_PHASES.P1)) {
		return minDateFromP1Start(assignment, previousAssignment, sixWeeksAgo);
	}

	// P2 -> P3
	// Anmelden P2 -> P3: - 42 Tage, >= Beginn P2
	if (targetPhase === DB_PHASES.P3 && assignment?.phaseId === DB_PHASES.P2) {
		// Phase 2 start
		const phaseStart = assignment?.consultationDate ? new Date(assignment.consultationDate) : null;

		const possibleDates = [sixWeeksAgo, phaseStart].filter((d): d is Date => !!d);
		return new Date(Math.max(...possibleDates.map((d) => d.getTime())));
	}

	// P2 -> P4
	// Abmelden P2 -> P4: - 42 Tage, frühestens neuester Kontakt, >= Beginn P2
	if (targetPhase === DB_PHASES.P4 && assignment?.phaseId === DB_PHASES.P2) {
		// Phase 2 start
		const phaseStart = assignment?.consultationDate ? new Date(assignment.consultationDate) : null;

		// Latest contact of assignment
		const latestContact = latestContactDate(assignment);
		const latestContactDay = latestContact ? new Date(latestContact) : null;

		const possibleDates = [sixWeeksAgo, phaseStart, latestContactDay].filter((d): d is Date => !!d);
		return new Date(Math.max(...possibleDates.map((d) => d.getTime())));
	}
	// P3 -> P4
	// Abmelden P3 -> P4: - 42 Tage,  >= Beginn P3, frühestens neuester Kontakt, frühestens Enddatum LEK
	if (targetPhase === DB_PHASES.P4 && assignment?.phaseId === DB_PHASES.P3) {
		const phaseStart = assignment?.careStartDate ? new Date(assignment.careStartDate) : null;

		const latestContact = latestContactDate(assignment);
		const latestContactDay = latestContact ? new Date(latestContact) : null;

		const latestLekEnd = latestLekEndDate(leks);
		const latestLekEndDay = latestLekEnd ? new Date(latestLekEnd) : null;

		const possibleDates = [sixWeeksAgo, phaseStart, latestContactDay, latestLekEndDay].filter((d): d is Date => !!d);

		return new Date(Math.max(...possibleDates.map((d) => d.getTime())));
	}
	// Default: 6 weeks ago
	return sixWeeksAgo;
}

export function minDateFromP1Start(assignment?: Assignment, previousAssignment?: Assignment, sixWeeksAgo?: Date) {
	if (previousAssignment?.cancellationDate) {
		const cancellationDate = new Date(previousAssignment.cancellationDate);
		return cancellationDate > sixWeeksAgo! ? cancellationDate : sixWeeksAgo!;
	}

	const contactStart = assignment?.contactStart ?? oldestContactDate(assignment!);
	if (contactStart) {
		const contactStartDate = new Date(contactStart);
		contactStartDate.setHours(0, 0, 0, 0);
		return contactStartDate > sixWeeksAgo! ? contactStartDate : sixWeeksAgo!;
	}

	return sixWeeksAgo!;
}

export function normalizeToStartOfDay(date: Date | string) {
	const d = new Date(date);
	d.setHours(0, 0, 0, 0);
	return d;
}

export function normalizeToEndOfDay(date: Date | string) {
	const d = new Date(date);
	d.setHours(23, 59, 59, 999);
	return d;
}

export function parseDate(value?: Date | string) {
	if (!value) return undefined;

	const d = new Date(value);
	return Number.isFinite(d.getTime()) ? d : undefined;
}

export function getFallbackDate(days: number) {
	const d = new Date();
	d.setDate(d.getDate() - days);
	return d;
}

export function normalizeDatePickerDateForUtcDisplay(value: Date): Date {
	return new Date(Date.UTC(value.getFullYear(), value.getMonth(), value.getDate()));
}
